import { useMutation, useQueryClient } from '@tanstack/react-query'

import { graphql_call as graphql_admin_call } from '../../schemas/server_admin_query'
import { graphql } from '../../schemas/server_admin_query'
import { clear_fund_caches } from '../helpers/fund_cache_helpers.ts'

export const mutation_create_fund_asset_transaction = graphql(`
  mutation create_fund_asset_transaction(
    $fund_id: ID!
    $asset_id: ID!
    $share_count: Float!
    $side: String!
    $spv: String
    $spv_share_class: String
    $share_class: String
    $fees: Float
    $total_price: Float!
    $executed_at: ISO8601DateTime
  ) {
    create_fund_asset_transaction(
      fund_id: $fund_id
      asset_id: $asset_id
      share_count: $share_count
      side: $side
      spv: $spv
      spv_share_class: $spv_share_class
      share_class: $share_class
      fees: $fees
      total_price: $total_price
      executed_at: $executed_at
    ) {
      errors
      fund_asset_transaction {
        id
        date
        price_per_share
        share_count
        side
        spv
        spv_share_class
        share_class
        fees
        total_price
        created_at
        updated_at
      }
    }
  }
`)

export interface FundAssetTransactionInput {
  fund_id: string
  asset_id: string
  share_count: number
  side: string
  spv?: string | null
  spv_share_class?: string | null
  share_class?: string | null
  fees?: number | null
  total_price: number
  executed_at?: string | null
}

export interface FundAssetTransactionMutationResponse {
  fund_asset_transaction: {
    id: string
    date: string
    price_per_share: number
    share_count: number
    side: string
    spv: string | null
    spv_share_class: string | null
    share_class: string | null
    fees: number | null
    total_price: number
    created_at: string
    updated_at: string
  } | null
  errors: string[]
}

export function useCreateFundAssetTransaction() {
  const queryClient = useQueryClient()

  return useMutation<
    FundAssetTransactionMutationResponse,
    Error,
    FundAssetTransactionInput
  >({
    // eslint-disable-next-line complexity
    mutationFn: async (
      input: FundAssetTransactionInput,
    ): Promise<FundAssetTransactionMutationResponse> => {
      try {
        const response = await graphql_admin_call({
          query: mutation_create_fund_asset_transaction,
          variables: {
            fund_id: input.fund_id,
            asset_id: input.asset_id,
            share_count: input.share_count,
            side: input.side,
            spv: input.spv || null,
            spv_share_class: input.spv_share_class || null,
            share_class: input.share_class || 'common',
            fees: input.fees || null,
            total_price: input.total_price,
            executed_at: input.executed_at || null,
          },
        })

        if (!response)
          throw new Error('Failed to create fund asset transaction')
        const result = response as FundAssetTransactionMutationResponse

        if (result.errors.length > 0) {
          throw new Error(result.errors.join(', '))
        }

        return result
      } catch (error) {
        console.error('Error creating fund asset transaction:', error)
        throw error
      }
    },
    onSuccess: async (data, variables) => {
      if (data.errors.length > 0) {
        return
      }
      await clear_fund_caches({
        queryClient,
        fundId: variables.fund_id,
      })
    },
  })
}
